/**
 * Certificate (classification societies, flag-state, OEM, internal QMS,
 * etc.) attached to a company, with an optional vessel- or
 * equipment-scoped target. Mirrors the backend Phase 5a Prisma model.
 *
 * Lifecycle: active → expiring_soon (within company-configured alert
 * window) → expired (past expiresAt). superseded when a newer cert
 * supersedes this one. revoked when withdrawn by the issuer.
 */
export interface Certificate {
  id: string;
  companyId: string;

  /** Discriminated by `scope`: vessel or equipment. */
  scope: CertificateScope;
  vesselId: string | null;
  equipmentId: string | null;

  certificateNumber: string;
  title: string;

  issuer: CertificateIssuer;
  issuerName: string;
  issuingAuthority: string | null;

  issuedAt: string; // ISO date-time
  validFrom: string;
  expiresAt: string;

  status: CertificateStatus;

  /** Chain of supersession — points to the cert this one replaces. */
  supersedesCertificateId: string | null;

  /** Revocation metadata — populated only when status='revoked'. */
  revokedAt: string | null;
  revokedByUserId: string | null;
  revocationReason: string | null;

  /** When the cert was renewed as part of a drydock campaign. */
  renewedDuringDrydockCampaignId: string | null;

  notes: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/**
 * Scope of the certificate. The backend enforces (via DB CHECK) that
 * exactly one of vesselId / equipmentId is set per scope.
 */
export const CERTIFICATE_SCOPES = ['vessel', 'equipment'] as const;
export type CertificateScope = (typeof CERTIFICATE_SCOPES)[number];

/**
 * Category of issuing authority — drives the i18n issuer label and
 * the icon shown in the badge column.
 */
export const CERTIFICATE_ISSUERS = [
  'classification_society',
  'flag_state',
  'manufacturer',
  'internal',
  'other',
] as const;
export type CertificateIssuer = (typeof CERTIFICATE_ISSUERS)[number];

/**
 * Lifecycle state. The expiration cron flips active→expiring_soon
 * and expiring_soon→expired automatically; superseded and revoked
 * are set explicitly via the dedicated endpoints.
 */
export const CERTIFICATE_STATUSES = [
  'active',
  'expiring_soon',
  'expired',
  'superseded',
  'revoked',
] as const;
export type CertificateStatus = (typeof CERTIFICATE_STATUSES)[number];

/**
 * Whether the certificate covers the whole vessel or a specific piece
 * of equipment. The two are mutually exclusive on the wire.
 */
export function isVesselScoped(c: Certificate): boolean {
  return c.scope === 'vessel';
}

/**
 * The status colour mapping used by the StatusBadge:
 *   active         → emerald
 *   expiring_soon  → amber
 *   expired        → red
 *   superseded     → muted
 *   revoked        → red
 */
export function statusVisualTone(
  status: CertificateStatus,
): 'emerald' | 'amber' | 'red' | 'muted' {
  switch (status) {
    case 'active':
      return 'emerald';
    case 'expiring_soon':
      return 'amber';
    case 'expired':
    case 'revoked':
      return 'red';
    case 'superseded':
      return 'muted';
  }
}
